import { useState } from "react";
import { toast } from "sonner";
import { Send, X, MessageCircle } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import DealCard from "./DealCard";

const DEFAULT_EMOJIS = ["👀", "🔥", "💸", "💯", "🙌"];

/**
 * A wishlist-scoped deal tile — pairs the standard DealCard with a public reactions strip.
 * Deal is expected to arrive with `comments_recent` (array, up to 3) and `comments_count` from
 * GET /api/wishlists/{slug}. Adding a reaction posts to /api/wishlists/{slug}/deals/{deal_id}/comments.
 */
export default function WishlistDealTile({ slug, deal, isWishlistOwner, emojis }) {
  const { user } = useAuth();
  const [comments, setComments] = useState(deal.comments_recent || []);
  const [total, setTotal] = useState(deal.comments_count || 0);
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [allComments, setAllComments] = useState(null); // populated when expanded
  const [rxCounts, setRxCounts] = useState(deal.reactions || {});
  const [myRx, setMyRx] = useState(deal.my_reactions || []);
  const [rxBusy, setRxBusy] = useState("");
  const emojiSet = (emojis && emojis.length ? emojis : DEFAULT_EMOJIS);

  const loadAll = async () => {
    try {
      const { data } = await api.get(`/wishlists/${slug}/comments`, { params: { deal_id: deal.id } });
      const list = (data?.by_deal?.[deal.id] || []).slice().reverse(); // oldest → newest
      setAllComments(list);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!user) { toast.error("Log in to leave a reaction."); return; }
    const clean = text.trim();
    if (!clean) return;
    setSubmitting(true);
    try {
      const { data } = await api.post(`/wishlists/${slug}/deals/${deal.id}/comments`, { text: clean });
      // Prepend to the visible strip (newest first, cap at 3)
      const updated = [data, ...comments].slice(0, 3);
      setComments(updated);
      setTotal((n) => n + 1);
      if (expanded) setAllComments((prev) => (prev ? [...prev, data] : [data]));
      setText("");
      toast.success("Reaction posted.");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSubmitting(false); }
  };

  const remove = async (comment) => {
    try {
      await api.delete(`/wishlists/${slug}/comments/${comment.id}`);
      setComments((all) => all.filter((c) => c.id !== comment.id));
      setAllComments((prev) => prev ? prev.filter((c) => c.id !== comment.id) : prev);
      setTotal((n) => Math.max(0, n - 1));
      toast.success("Reaction removed.");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const canDelete = (c) => user && (c.user_id === user.id || isWishlistOwner);
  const visible = expanded && allComments ? allComments : comments.slice().reverse();
  const showMore = total > (comments?.length || 0) && !expanded;

  const toggleReaction = async (emoji) => {
    if (!user) { toast.error("Log in to react."); return; }
    setRxBusy(emoji);
    // Optimistic
    const mine = new Set(myRx);
    const counts = { ...rxCounts };
    const had = mine.has(emoji);
    if (had) { mine.delete(emoji); counts[emoji] = Math.max(0, (counts[emoji] || 1) - 1); }
    else { mine.add(emoji); counts[emoji] = (counts[emoji] || 0) + 1; }
    setMyRx(Array.from(mine));
    setRxCounts(counts);
    try {
      const { data } = await api.post(`/wishlists/${slug}/deals/${deal.id}/reactions`, { emoji });
      setRxCounts(data.counts || {});
      setMyRx(data.mine || []);
    } catch (err) {
      // Revert
      setMyRx(myRx);
      setRxCounts(rxCounts);
      toast.error(formatApiError(err));
    } finally {
      setRxBusy("");
    }
  };

  return (
    <div className="flex flex-col" data-testid={`wishlist-tile-${deal.id}`}>
      <DealCard deal={deal} />
      <div className="mt-3 border-2 border-black/10 bg-white p-3 flex-1" data-testid={`wishlist-comments-${deal.id}`}>
        {/* Emoji reaction row */}
        <div className="mb-3 pb-3 border-b border-black/5" data-testid={`wishlist-reactions-${deal.id}`}>
          <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-black/50 mb-2">Quick reactions</div>
          <div className="flex items-center gap-1.5 flex-wrap">
            {emojiSet.map((emoji) => {
              const active = myRx.includes(emoji);
              const count = rxCounts[emoji] || 0;
              return (
                <button
                  key={emoji}
                  type="button"
                  disabled={rxBusy === emoji}
                  onClick={() => toggleReaction(emoji)}
                  className={`inline-flex items-center gap-1 px-2 py-1 text-sm border-2 transition-transform hover:scale-105 disabled:opacity-50 ${
                    active
                      ? "bg-[#FFF5F6] border-[#E63946]"
                      : "bg-white border-black/10 hover:border-black/40"
                  }`}
                  data-testid={`reaction-${emoji}-${deal.id}`}
                  aria-label={`React with ${emoji}`}
                  aria-pressed={active}
                >
                  <span className="text-base leading-none">{emoji}</span>
                  {count > 0 && (
                    <span className="text-[10px] font-mono font-bold tracking-widest text-black/70">
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-black/50 inline-flex items-center gap-1">
            <MessageCircle className="w-3 h-3" strokeWidth={2} />
            Reactions
            {total > 0 && <span className="ml-1 text-[#E63946]">{total}</span>}
          </div>
          {showMore && (
            <button
              type="button"
              onClick={async () => { setExpanded(true); await loadAll(); }}
              className="text-[10px] font-bold uppercase tracking-widest text-[#E63946] hover:underline"
              data-testid={`wishlist-comments-expand-${deal.id}`}
            >
              +{total - comments.length} more
            </button>
          )}
        </div>

        <ul className="space-y-2 mb-3">
          {visible.length === 0 ? (
            <li className="text-xs text-black/40 font-serif italic" data-testid={`wishlist-comments-empty-${deal.id}`}>
              Be the first to react to this pick.
            </li>
          ) : visible.map((c) => (
            <li key={c.id} className="flex items-start gap-2 text-sm" data-testid={`wishlist-comment-${c.id}`}>
              <span className="w-6 h-6 flex-shrink-0 bg-[#111111] text-white text-[10px] font-bold flex items-center justify-center">
                {(c.user_name || "?").slice(0, 1).toUpperCase()}
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] font-bold uppercase tracking-widest text-black/60">
                  {c.user_name}
                </div>
                <p className="font-serif text-sm text-black/80 leading-snug break-words">{c.text}</p>
              </div>
              {canDelete(c) && (
                <button
                  type="button"
                  onClick={() => remove(c)}
                  className="text-black/30 hover:text-[#E63946]"
                  data-testid={`wishlist-comment-delete-${c.id}`}
                  title="Delete reaction"
                >
                  <X className="w-3.5 h-3.5" strokeWidth={2} />
                </button>
              )}
            </li>
          ))}
        </ul>

        {user ? (
          <form onSubmit={submit} className="flex items-center gap-2" data-testid={`wishlist-comment-form-${deal.id}`}>
            <input
              value={text}
              onChange={(e) => setText(e.target.value.slice(0, 140))}
              placeholder="React in 140 chars…"
              className="flex-1 border border-black/20 focus:border-black outline-none px-3 py-2 font-serif text-sm bg-white"
              data-testid={`wishlist-comment-input-${deal.id}`}
            />
            <button
              type="submit"
              disabled={submitting || !text.trim()}
              className="bg-[#111111] text-white px-3 py-2 hover:bg-[#E63946] disabled:opacity-50"
              data-testid={`wishlist-comment-submit-${deal.id}`}
              aria-label="Post reaction"
            >
              <Send className="w-3.5 h-3.5" strokeWidth={2} />
            </button>
          </form>
        ) : (
          <div className="text-[10px] font-mono uppercase tracking-widest text-black/40" data-testid={`wishlist-comment-login-${deal.id}`}>
            Log in to react
          </div>
        )}
      </div>
    </div>
  );
}
